import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Users, Bot, Activity, Zap, MessageSquare, Plus, Settings, Edit, Trash2 } from 'lucide-react';
import { API_URL, SOCKET_URL } from '@/config/api';
import { authFetch } from '@/utils/auth-fetch';
import { format } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';

interface AgentMessage {
  id: string;
  group_id: string;
  sender_agent_id: string;
  sender_name: string;
  sender_emoji: string;
  message_type: string;
  content: string;
  metadata: any;
  priority: string;
  created_at: string;
  thread_id?: string;
  reply_to_message_id?: string;
}

interface ConversationGroup {
  id: string;
  name: string;
  description: string;
  type: string;
  emoji: string;
  conductor_channel?: string;
  is_active: boolean;
  settings?: any;
  created_at: string;
  updated_at?: string;
  site_id?: string;
}

interface AgentInfo {
  id: string;
  name: string;
  emoji: string;
  status: 'online' | 'offline' | 'busy';
  lastSeen?: string;
}

const AGENT_EMOJIS: Record<string, string> = {
  'page-chatbot-agent': '💬',
  'insights-hub-agent': '🎯',
  'customer-intent-agent': '🧠',
  'page-semantic-intent-agent': '📊',
  'content-gap-detector': '🔍',
  'orchestration-agent': '🎼',
  'voice-assistant-agent': '🎤',
  'site-crawler-agent': '🕷️',
  'competitive-analysis-agent': '⚔️',
  'session-summarizer-agent': '📝',
  'keyword-extractor-agent': '🔑',
  'adjacent-topics-agent': '🔗',
  'default': '🤖'
};

export function AgentConversations() {
  const { selectedSite } = useSite();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [groups, setGroups] = useState<ConversationGroup[]>([]);
  // Use conductor channel slug by default; conductor expects slugs, not numeric ids
  const [activeGroup, setActiveGroup] = useState<string>('conversations');
  const [onlineAgents, setOnlineAgents] = useState<AgentInfo[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ConversationGroup | null>(null);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    emoji: '💬',
    type: 'custom',
    conductorChannel: ''
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load initial data
  useEffect(() => {
    loadGroups();
    loadRecentMessages();
  }, []);
  
  // Reload messages when active group changes
  useEffect(() => {
    if (activeGroup) {
      loadRecentMessages();
    }
  }, [activeGroup]);

  // Setup WebSocket connection
  useEffect(() => {
    // Use SOCKET_URL for Socket.IO connection, fallback to current host if empty
    const socketUrl = SOCKET_URL || window.location.origin;
    console.log('Connecting to Socket.IO at:', socketUrl);
    
    const newSocket = io(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'], // Try websocket first, fallback to polling
      auth: {
        token: localStorage.getItem('jwt')
      }
    });

    newSocket.on('connect', () => {
      console.log('Connected to agent conversations');
      setIsConnected(true);
      newSocket.emit('join-agent-conversations');
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from agent conversations');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error.message);
      console.error('Error type:', error.type);
      console.error('Error details:', error);
    });

    newSocket.on('agent-message', (message: AgentMessage) => {
      console.log('New agent message:', message);
      setMessages(prev => [...prev, message]);
    });

    newSocket.on('agent-status', (agents: AgentInfo[]) => {
      setOnlineAgents(agents);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const getChannelKey = (group: ConversationGroup): string => {
    // Prefer explicit conductor_channel slug; fallback to known defaults; never fallback to numeric id
    if (group.conductor_channel && group.conductor_channel.trim().length > 0) {
      return group.conductor_channel;
    }
    // Map common group ids to channel slugs
    const idToSlug: Record<string, string> = {
      'conversations': 'conversations',
      'intelligence-feed': 'intelligence-feed',
      'content-team': 'content-team',
      'growth-team': 'growth-team',
      'research-team': 'research-team',
      'support-team': 'support-team',
      'site-operations': 'site-operations'
    };
    if (idToSlug[group.id]) return idToSlug[group.id];
    return 'conversations';
  };

  const loadGroups = async () => {
    try {
      if (!selectedSite) {
        // Load default groups if no site selected
        const response = await authFetch('/conversation-groups/defaults');
        const data = await response.json();
        if (data.success) {
          setGroups(data.groups);
          const first = data.groups[0];
          if (first && (!activeGroup || !data.groups.some(g => getChannelKey(g) === activeGroup))) {
            setActiveGroup(getChannelKey(first));
          }
        }
        return;
      }

      // Load groups for the selected site
      const response = await authFetch(`/conversation-groups/site/${selectedSite.id}`);
      const data = await response.json();
      
      if (data.success) {
        setGroups(data.groups);
        const first = data.groups[0];
        if (first && (!activeGroup || !data.groups.some(g => getChannelKey(g) === activeGroup))) {
          setActiveGroup(getChannelKey(first));
        }
      } else {
        // Fallback to defaults if no groups found
        const defaultResponse = await authFetch('/conversation-groups/defaults');
        const defaultData = await defaultResponse.json();
        if (defaultData.success) {
          setGroups(defaultData.groups);
          const first = defaultData.groups[0];
          if (first) setActiveGroup(getChannelKey(first));
        }
      }
    } catch (error) {
      console.error('Failed to load groups:', error);
      // Fallback to hardcoded groups
      const fallbackGroups = [
        { id: 'conversations', name: 'Conversations', description: 'General agent conversations', emoji: '💬', type: 'system', conductor_channel: 'conversations', is_active: true, created_at: new Date().toISOString() },
        { id: 'intelligence-feed', name: 'Intelligence Feed', description: 'AI insights and analysis', emoji: '🧠', type: 'system', conductor_channel: 'intelligence-feed', is_active: true, created_at: new Date().toISOString() }
      ];
      setGroups(fallbackGroups);
      setActiveGroup('conversations');
    }
  };

  const loadRecentMessages = async () => {
    try {
      const response = await authFetch(`/conductor/channels/${activeGroup}/messages?limit=50`);
      const data = await response.json();
      if (data.success && data.data) {
        // Transform conductor messages to our format
        const transformedMessages = (data.data.messages || []).map((msg: any) => ({
          id: msg.id,
          group_id: activeGroup,
          sender_agent_id: msg.agentName,
          sender_name: msg.agentName,
          sender_emoji: getAgentEmoji(msg.agentName),
          message_type: msg.messageType,
          content: msg.text,
          metadata: {},
          priority: 'medium',
          created_at: msg.timestamp,
          thread_id: msg.threadId
        }));
        setMessages(transformedMessages);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      setMessages([]);
    }
  };

  const getAgentEmoji = (agentId: string) => {
    return AGENT_EMOJIS[agentId] || AGENT_EMOJIS.default;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Agent avatar mapping
  const getAgentAvatar = (agentName: string): string => {
    const avatarMap: Record<string, string> = {
      'insights-hub': '/crew-agents/insight-hub.jpg',
      'insights-hub-agent': '/crew-agents/insight-hub.jpg',
      'page-semantic-intent': '/crew-agents/page-semantic.png',
      'page-semantic-intent-agent': '/crew-agents/page-semantic.png',
      'site-crawler': '/crew-agents/crawler.jpg',
      'site-crawler-agent': '/crew-agents/crawler.jpg',
      'page-chatbot': '/crew-agents/chatbot.jpg',
      'page-chatbot-agent': '/crew-agents/chatbot.jpg',
      'chatbot': '/crew-agents/chatbot.jpg',
      'topic-explorer': '/crew-agents/topic-research.jpg',
      'research-agent': '/crew-agents/topic-research.jpg',
      'customer-intent': '/crew-agents/customer-intent.jpg',
      'customer-intent-agent': '/crew-agents/customer-intent.jpg',
      'behavior-analyst': '/crew-agents/behavior-analyst.jpg',
      'behavior-analyst-agent': '/crew-agents/behavior-analyst.jpg',
      'growth-analyst': '/crew-agents/growth-analyst.jpg',
      'growth-analyst-agent': '/crew-agents/growth-analyst.jpg',
      'conductor': '/crew-agents/conductor-agent.jpg',
      'conductor-agent': '/crew-agents/conductor-agent.jpg',
      'orchestration': '/crew-agents/orchestration.jpg',
      'orchestration-agent': '/crew-agents/orchestration.jpg',
      'onboarding': '/crew-agents/onboarding.jpg',
      'onboarding-agent': '/crew-agents/onboarding.jpg',
      'keyword-extractor': '/crew-agents/keyword-extractor.jpg',
      'keyword-extractor-agent': '/crew-agents/keyword-extractor.jpg',
      'session-summarizer': '/crew-agents/session-summarizer.jpg',
      'session-summarizer-agent': '/crew-agents/session-summarizer.jpg',
      'voicebot': '/crew-agents/voicebot.jpg',
      'voice-assistant-agent': '/crew-agents/voicebot.jpg',
      'competitive-analysis': '/crew-agents/competitors.png',
      'competitive-analysis-agent': '/crew-agents/competitors.png',
      'intent-based-recommender': '/crew-agents/intent-based-recommender.jpg',
      'intent-based-recommender-agent': '/crew-agents/intent-based-recommender.jpg',
      'narrative-presentation': '/crew-agents/narrator.png',
      'narrative-presentation-agent': '/crew-agents/narrator.png',
      'question-suggestion': '/crew-agents/suggestions-with-answers.jpg',
      'question-suggestion-agent': '/crew-agents/suggestions-with-answers.jpg',
      'style-suggestion': '/crew-agents/style-suggestion.jpg',
      'style-suggestion-agent': '/crew-agents/style-suggestion.jpg',
      'auto-evaluator': '/crew-agents/auto-evaluator.jpg',
      'auto-evaluator-agent': '/crew-agents/auto-evaluator.jpg',
      'adjacent-topics': '/crew-agents/adjacent.jpg',
      'adjacent-topics-agent': '/crew-agents/adjacent.jpg',
      'page-role': '/crew-agents/page-role.jpg',
      'page-role-agent': '/crew-agents/page-role.jpg',
      'site-sector-analyzer': '/crew-agents/sector-assigner.jpg',
      'site-sector-classifier': '/crew-agents/site-sector-classifier.jpg'
    };
    
    const normalizedName = agentName.toLowerCase();
    // Try exact match first
    if (avatarMap[normalizedName]) {
      return avatarMap[normalizedName];
    }
    
    // Try without -agent suffix
    const withoutAgent = normalizedName.replace(/-agent$/, '');
    if (avatarMap[withoutAgent]) {
      return avatarMap[withoutAgent];
    }
    
    // Default avatar
    return '/crew-agents/chatbot.jpg';
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'notification':
        return <Activity className="h-3 w-3" />;
      case 'action':
        return <Zap className="h-3 w-3" />;
      case 'insight':
        return <MessageSquare className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const renderMessage = (message: AgentMessage) => {
    const isSystemMessage = message.message_type === 'notification' || message.message_type === 'status';
    
    return (
      <div
        key={message.id}
        className={`flex gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 ${
          isSystemMessage ? 'opacity-75' : ''
        }`}
      >
        <Avatar className="h-8 w-8">
          <AvatarImage 
            src={getAgentAvatar(message.sender_agent_id || message.sender_name)} 
            alt={message.sender_name}
            className="object-cover"
          />
          <AvatarFallback>{message.sender_emoji || getAgentEmoji(message.sender_agent_id)}</AvatarFallback>
        </Avatar>
        
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{message.sender_name}</span>
            {message.priority !== 'medium' && (
              <Badge variant={getPriorityColor(message.priority)} className="text-xs">
                {message.priority}
              </Badge>
            )}
            {getMessageTypeIcon(message.message_type)}
            <span className="text-xs text-gray-500">
              {format(new Date(message.created_at), 'HH:mm')}
            </span>
          </div>
          
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {message.content}
          </div>
          
          {message.metadata?.link && (
            <a
              href={message.metadata.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline"
            >
              View Details →
            </a>
          )}
        </div>
      </div>
    );
  };

  // Group management functions
  const createGroup = async () => {
    if (!selectedSite || !newGroup.name.trim()) return;
    
    try {
      const response = await authFetch(`/conversation-groups/site/${selectedSite.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newGroup.name,
          description: newGroup.description,
          emoji: newGroup.emoji,
          type: newGroup.type,
          conductorChannel: newGroup.conductorChannel
        })
      });
      
      const data = await response.json();
      if (data.success) {
        await loadGroups();
        setShowGroupModal(false);
        setNewGroup({
          name: '',
          description: '',
          emoji: '💬',
          type: 'custom',
          conductorChannel: ''
        });
      }
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  const updateGroup = async () => {
    if (!editingGroup) return;
    
    try {
      const response = await authFetch(`/conversation-groups/${editingGroup.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newGroup.name,
          description: newGroup.description,
          emoji: newGroup.emoji,
          type: newGroup.type,
          conductorChannel: newGroup.conductorChannel
        })
      });
      
      const data = await response.json();
      if (data.success) {
        await loadGroups();
        setShowGroupModal(false);
        setEditingGroup(null);
        setNewGroup({
          name: '',
          description: '',
          emoji: '💬',
          type: 'custom',
          conductorChannel: ''
        });
      }
    } catch (error) {
      console.error('Failed to update group:', error);
    }
  };

  const deleteGroup = async (groupId: string) => {
    if (!confirm('Are you sure you want to delete this group?')) return;
    
    try {
      const response = await authFetch(`/conversation-groups/${groupId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        await loadGroups();
        if (activeGroup === groupId) {
          setActiveGroup('conversations');
        }
      }
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  const openEditModal = (group: ConversationGroup) => {
    setEditingGroup(group);
    setNewGroup({
      name: group.name,
      description: group.description || '',
      emoji: group.emoji,
      type: group.type,
      conductorChannel: group.conductor_channel || ''
    });
    setShowGroupModal(true);
  };

  const openCreateModal = () => {
    setEditingGroup(null);
    setNewGroup({
      name: '',
      description: '',
      emoji: '💬',
      type: 'custom',
      conductorChannel: ''
    });
    setShowGroupModal(true);
  };

  const activeGroupData = groups.find(g => g.id === activeGroup);
  const groupMessages = messages.filter(m => m.group_id === activeGroup);

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-64 border-r bg-gray-50 dark:bg-gray-900">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Agent Teams
            </h3>
            {selectedSite && (
              <Button
                variant="ghost"
                size="sm"
                onClick={openCreateModal}
                className="h-6 w-6 p-0"
              >
                <Plus className="h-3 w-3" />
              </Button>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
            <span className="text-xs text-gray-600">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>
        
        <ScrollArea className="h-[calc(100%-5rem)]">
          <div className="p-2 space-y-1">
            {groups.map(group => (
              <div
                key={group.id}
                className={`relative group rounded-lg transition-colors ${
                  activeGroup === getChannelKey(group)
                    ? 'bg-white dark:bg-gray-800 shadow-sm'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <button
                  onClick={() => setActiveGroup(getChannelKey(group))}
                  className="w-full text-left p-3 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{group.emoji}</span>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{group.name}</div>
                      <div className="text-xs text-gray-500">{group.description}</div>
                    </div>
                  </div>
                </button>
                
                {group.type === 'custom' && selectedSite && (
                  <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(group);
                        }}
                        className="h-6 w-6 p-0"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteGroup(group.id);
                        }}
                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{activeGroupData?.emoji}</span>
              <div>
                <h2 className="font-semibold">{activeGroupData?.name}</h2>
                <p className="text-sm text-gray-500">{activeGroupData?.description}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1">
                <Users className="h-3 w-3" />
                {onlineAgents.length} agents online
              </Badge>
            </div>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-1">
            {groupMessages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No messages yet in this group</p>
                <p className="text-sm mt-2">Agents will start conversing as they process tasks</p>
              </div>
            ) : (
              groupMessages.map(renderMessage)
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Online Agents */}
        <div className="border-t p-4 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <span className="font-medium">Active Agents:</span>
            <div className="flex items-center gap-2 flex-wrap">
              {onlineAgents.map(agent => (
                <div key={agent.id} className="flex items-center gap-1">
                  <div className={`h-2 w-2 rounded-full ${
                    agent.status === 'online' ? 'bg-green-500' : 
                    agent.status === 'busy' ? 'bg-yellow-500' : 'bg-gray-400'
                  }`} />
                  <span>{agent.emoji}</span>
                  <span>{agent.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Group Management Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? 'Edit Group' : 'Create New Group'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="groupName">Name</Label>
              <Input
                id="groupName"
                value={newGroup.name}
                onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter group name..."
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="groupDescription">Description</Label>
              <Textarea
                id="groupDescription"
                value={newGroup.description}
                onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter group description..."
                rows={3}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="groupEmoji">Emoji</Label>
              <Input
                id="groupEmoji"
                value={newGroup.emoji}
                onChange={(e) => setNewGroup(prev => ({ ...prev, emoji: e.target.value }))}
                placeholder="💬"
                maxLength={2}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="groupType">Type</Label>
              <Select
                value={newGroup.type}
                onValueChange={(value) => setNewGroup(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select group type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="temporary">Temporary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="conductorChannel">Conductor Channel</Label>
              <Input
                id="conductorChannel"
                value={newGroup.conductorChannel}
                onChange={(e) => setNewGroup(prev => ({ ...prev, conductorChannel: e.target.value }))}
                placeholder="channel-name"
              />
              <p className="text-xs text-gray-500">
                Optional: Maps to a specific conductor/Kafka channel
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowGroupModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={editingGroup ? updateGroup : createGroup}
              disabled={!newGroup.name.trim()}
            >
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}